<template>
  <v-container fluid class="pa-0 step-container">
    <v-col cols="12" class="pb-0 mb-n4">
      <v-container fluid class="d-flex pa-0">
        <v-col cols="12" class="d-flex pa-0">
          <v-col cols="12" class="d-flex align-center pt-0 pb-0">
            <v-select
              clearable
              deletable-chips
              small-chips
              solo
              multiple
              hide-details
              single-line
              height="50"
              class="reasons-select"
              label="Filter by ignore reason"
              :loading="ignoreReasonsLoading"
              :items="ignoreReasons"
              v-model="selectedReasons"
            />
            <v-btn
              outlined
              class="ml-10"
              color="success"
              @click="handleFilterApply"
            >
              Apply
            </v-btn>
          </v-col>
        </v-col>
      </v-container>
    </v-col>
    <VirtualizeTable
      show-pagination
      show-all-option
      group-by="comment"
      group-title-prefix="Reason of ignoring: "
      :items="rows"
      :headers="HeadersList"
      :loading="loading"
      :pagination="pagination"
      @onChangeItemsPerPage="handleItemsPerPageChange"
      @onChangePage="handlePageChange"
    >
      <!-- Notes -->
      <template #migrationPoll.notes="{ item }">
        <v-edit-dialog v-if="!editDisabled">
          <span class="caption" v-if="!$v(item, 'migrationPoll.notes')">
            Click to type notes...
          </span>
          <span class="caption" v-else>
            {{ $v(item, 'migrationPoll.notes') }}
          </span>
          <template v-slot:input>
            <v-text-field
              counter
              single-line
              label="Edit note"
              :value="$v(item, 'migrationPoll.notes')"
              @change="
                (value) =>
                  handleMigrationPollChange(item.id, { notes: value.trim() })
              "
            />
          </template>
        </v-edit-dialog>
        <span class="caption" v-else>
          {{ $v(item, 'migrationPoll.notes') }}
        </span>
      </template>

      <!-- GL Code -->
      <template #custom_gl_code="{ item }">
        <v-edit-dialog
          v-if="
            !editDisabled &&
            item.status.name !== RowStatusesNamesMap.IMPORT_VERIFIED_BY_CUSTOMER
          "
        >
          <span class="caption" v-if="!$v(item, 'custom_gl_code')">
            Edit...
          </span>
          <span class="caption" v-else>{{ $v(item, 'custom_gl_code') }}</span>
          <template v-slot:input>
            <v-text-field
              counter
              single-line
              label="Edit note"
              :value="$v(item, 'custom_gl_code')"
              @change="
                (value) =>
                  handleChange(item.id, { custom_gl_code: value.trim() }, true)
              "
            />
          </template>
        </v-edit-dialog>
        <span class="caption" v-else>{{ $v(item, 'custom_gl_code') }}</span>
      </template>

      <!-- Type override -->
      <template #mapped_core_type_override="{ item }">
        <v-edit-dialog
          large
          v-if="
            !editDisabled &&
            item.status.name !== RowStatusesNamesMap.IMPORT_VERIFIED_BY_CUSTOMER
          "
          @cancel="handleMappedTypeOverrideCancel"
          @save="() => handleMappedTypeOverrideSave(item.id)"
        >
          <span class="caption" v-if="!$v(item, 'mapped_core_type_override')">
            Edit...
          </span>
          <span class="caption" v-else>
            {{ $v(item, 'mapped_core_type_override') }}
          </span>
          <template v-slot:input>
            <v-select
              clearable
              dense
              flat
              class="mt-5 override-type-input"
              label="Override type"
              :items="CategoriesList"
              v-model="selectedMappedTypeOverride"
            />
          </template>
        </v-edit-dialog>
        <span class="caption" v-else>
          {{ $v(item, 'mapped_core_type_override') }}
        </span>
      </template>

      <!-- Not ignore -->
      <template #migrationPoll.checked="{ item }">
        <v-checkbox
          :disabled="checkIsDisabledNotIgnore(item)"
          :input-value="$v(item, 'migrationPoll.checked')"
          @change="(checked) => handleNotIgnoreChange(checked, item)"
        />
      </template>
    </VirtualizeTable>
    <v-snackbar color="success" timeout="1500" v-model="snackbar">
      The row has been updated
    </v-snackbar>
  </v-container>
</template>

<script>
import _ from 'lodash';
import VirtualizeTable from '@/components/common/VirtualizeTable';
import {
  StepsStatusesMap,
  CategoriesList,
  RowStatusesNamesMap
} from '@/constants';
import { processingResultsApiV1 } from '@/api/v1';
import { processingResultsApiV2 } from '@/api/v2';

const HeadersList = [
  {
    text: 'Not ignore',
    value: 'migrationPoll.checked',
    align: 'center',
    width: 75
  },
  {
    text: 'Category',
    value: 'orig_cat1',
    align: 'center'
  },
  {
    text: 'Id',
    value: 'orig_code',
    align: 'center'
  },
  {
    text: 'Name',
    value: 'orig_name',
    align: 'center'
  },
  {
    text: 'Price',
    value: 'priceInfo.price',
    align: 'center'
  },
  {
    text: 'Last sold',
    value: 'priceInfo.lastsold',
    align: 'center'
  },
  {
    text: 'GL Code',
    value: 'custom_gl_code',
    align: 'center',
    width: 100
  },
  {
    text: 'Type override',
    value: 'mapped_core_type_override',
    align: 'center',
    width: 175
  },
  {
    text: 'Notes',
    value: 'migrationPoll.notes',
    align: 'center',
    justify: 'flex-end'
  }
];

export default {
  props: {
    currentStep: {
      type: Object,
      default: () => ({})
    },
    rowStatuses: {
      type: Array,
      default: () => []
    }
  },
  data: () => ({
    StepsStatusesMap,
    HeadersList,
    CategoriesList,
    RowStatusesNamesMap,
    resultId: null,
    rows: [],
    loading: false,
    pagination: {
      page: 1,
      pageCount: 2,
      pageSize: 200,
      rowCount: 0
    },
    snackbar: false,
    ignoreReasons: [],
    ignoreReasonsLoading: false,
    selectedReasons: [],
    selectedMappedTypeOverride: null
  }),
  components: {
    VirtualizeTable
  },
  computed: {
    editDisabled() {
      return (
        this.loading || this.currentStep.status === StepsStatusesMap.SIGNED
      );
    }
  },
  async mounted() {
    this.resultId = this.$route.params.result_id;
    await this.getRows();
    await this.getIgnoredReasons();
  },
  watch: {
    selectedReasons(reasons) {
      console.log(reasons);
    }
  },
  methods: {
    async getRows() {
      this.loading = true;
      const resultId = this.resultId;
      const isFetchAll = this.pagination.pageSize === 'All';
      const stepParams = _.get(this.currentStep, 'params', {});
      const defaultParams = this.selectedReasons.length
        ? { comment: this.selectedReasons }
        : {};
      const params = isFetchAll
        ? stepParams
        : {
            page: this.pagination.page,
            rowsPerPage: this.pagination.pageSize,
            ...stepParams
          };

      const { items, pagination } = await (
        this.$store.getters.isMatcherApiV2Enabled
          ? processingResultsApiV2
          : processingResultsApiV1
      ).fetchRows(
        resultId,
        {
          ...defaultParams,
          ...params
        },
        isFetchAll
      );

      this.rows = items;

      if (pagination) {
        this.pagination = pagination;
      }

      this.loading = false;
    },
    async getIgnoredReasons() {
      this.ignoreReasonsLoading = true;
      this.ignoreReasons = await (
        this.$store.getters.isMatcherApiV2Enabled
          ? processingResultsApiV2
          : processingResultsApiV1
      ).fetchResultRowsUniqueComments(this.resultId, 'ignore');
      this.ignoreReasonsLoading = false;
    },
    async handleChange(
      resultRowId,
      updated,
      shouldUpdateStatus,
      statusName = RowStatusesNamesMap.NEEDS_CHANGE
    ) {
      this.loading = true;
      const status = this.rowStatuses.find(({ name }) => name === statusName);

      const updatedRow = await (
        this.$store.getters.isMatcherApiV2Enabled
          ? processingResultsApiV2
          : processingResultsApiV1
      ).updateRow(this.$route.params.result_id, resultRowId, {
        ...updated,
        ...(shouldUpdateStatus ? { status } : {})
      });

      _.merge(_.find(this.rows, { id: resultRowId }), updatedRow);

      if (this.currentStep.status !== StepsStatusesMap.IN_PROGRESS) {
        this.$emit('onUpdateStepStatus', StepsStatusesMap.IN_PROGRESS);
      }

      this.loading = false;
      this.snackbar = true;
    },
    async handleMigrationPollChange(resultRowId, updated) {
      await this.handleChange(resultRowId, {
        migrationPoll: updated
      });
    },
    async handleMappedTypeOverrideSave(resultRowId) {
      if (this.selectedMappedTypeOverride) {
        await this.handleChange(
          resultRowId,
          { mapped_core_type_override: this.selectedMappedTypeOverride },
          true
        );
        this.selectedMappedTypeOverride = null;
      }
    },
    async handleNotIgnoreChange(checked, resultRow) {
      const { id, status_id, previous_status_id } = _.pick(resultRow, [
        'id',
        'status_id',
        'previous_status_id'
      ]);
      const previousStatusName = _.get(
        _.find(this.rowStatuses, { id: parseInt(previous_status_id, 10) }),
        'name'
      );

      await this.handleChange(
        id,
        {
          previous_status_id: checked ? status_id : null,
          migrationPoll: {
            checked
          }
        },
        true,
        checked
          ? RowStatusesNamesMap.IMPORT_VERIFIED_BY_CUSTOMER
          : previousStatusName
      );
    },
    checkIsDisabledNotIgnore(item) {
      return (
        this.editDisabled ||
        item.status.name === RowStatusesNamesMap.NEEDS_CHANGE ||
        item.status.name === RowStatusesNamesMap.NEEDS_CODE_CHANGE ||
        item.status.name === RowStatusesNamesMap.READY_FOR_RE_MATCH
      );
    },
    handleMappedTypeOverrideCancel() {
      this.selectedMappedTypeOverride = null;
    },
    handleItemsPerPageChange(itemsPerPage) {
      this.pagination.page = 1;
      this.pagination.pageSize = itemsPerPage;
      this.getRows();
    },
    handlePageChange(pageNum) {
      if (this.pagination.page !== pageNum) {
        this.pagination.page = pageNum;
        this.getRows();
      }
    },
    handleFilterApply() {
      this.pagination.page = 1;
      this.getRows();
    }
  }
};
</script>

<style lang="scss">
.reasons-select {
  width: 1px;

  .v-select__selections {
    flex-wrap: nowrap;
    overflow-y: auto;
    &::-webkit-scrollbar {
      display: none;
    }

    .v-chip {
      display: inline-flex;
      flex-shrink: 0;
    }
  }
}
.override-type-input {
  max-width: 175px;
}
</style>
